import { useCallback, useEffect, useState } from 'react';
import { sites as loadSites, type SiteSummary } from '../../api';
import {
  providers as loadProviders,
  providersHealth,
  saveProvider,
  type ProviderHealth,
  type ProviderView,
} from './api';
import type { PaymentsKey, PaymentsTranslate } from './i18n';
import { StatusBadge, Table, TableSkeleton, Field, Select, TextInput } from '../../shell/kit';
import { useToast } from '../../shell/toast';
import { useConfirm } from '../../shell/confirm';

const FIELD = 'w-full rounded border border-line-strong px-2 py-1 text-sm';
const LABEL = 'mb-1 block text-xs text-ink-muted';

export interface ProvidersTabProps {
  readonly t: PaymentsTranslate;
  readonly onError: (message: string) => void;
}

export function ProvidersTab({ t, onError }: ProvidersTabProps) {
  const [rows, setRows] = useState<readonly ProviderView[]>([]);
  const [health, setHealth] = useState<readonly ProviderHealth[]>([]);
  const [adapters, setAdapters] = useState<readonly string[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [secret, setSecret] = useState<string | undefined>(undefined);

  const toast = useToast();
  const confirm = useConfirm();
  const [code, setCode] = useState('');
  const [kind, setKind] = useState<ProviderView['kind']>('acquiring');
  const [adapter, setAdapter] = useState('fake');
  const [endpoint, setEndpoint] = useState('');
  const [credentialEnv, setCredentialEnv] = useState('');
  const [siteId, setSiteId] = useState('');
  const [settingsText, setSettingsText] = useState('');
  const [siteList, setSiteList] = useState<readonly SiteSummary[]>([]);

  useEffect(() => {
    void loadSites()
      .then(setSiteList)
      .catch(() => setSiteList([]));
  }, []);

  const reload = useCallback(async (): Promise<void> => {
    setLoading(true);
    try {
      const [list, state] = await Promise.all([loadProviders(), providersHealth()]);
      // Код pos — не эквайринговый провайдер, а служебная запись-якорь для
      // зеркала кассовых операций в «Платежах» (payments/pos-mirror): её
      // нечем и незачем управлять здесь — ни секрета, ни адаптера в смысле
      // этой вкладки у неё нет.
      setRows(list.providers.filter((p) => p.code !== 'pos'));
      setHealth(state.providers.filter((p) => p.provider !== 'pos'));
      setAdapters(state.adapters);
    } catch (err) {
      onError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }, [onError]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const envLooksLikeName =
    credentialEnv.trim() === '' || /^[A-Z][A-Z0-9_]*$/.test(credentialEnv.trim());

  /** «имя=значение» по строке → параметры адаптера; кривая строка — ошибка. */
  const parsedSettings = ((): { error: string } | { settings: Record<string, string> } => {
    const out: Record<string, string> = {};
    for (const raw of settingsText.split('\n')) {
      const line = raw.trim();
      if (!line) continue;
      const at = line.indexOf('=');
      const key = at > 0 ? line.slice(0, at).trim() : '';
      if (!/^[a-zA-Z][a-zA-Z0-9_]*$/.test(key)) return { error: line };
      out[key] = line.slice(at + 1).trim();
    }
    return { settings: out };
  })();

  /**
   * Сохранение и перевыпуск секрета разведены. Раньше это была одна кнопка
   * «Сохранить · Перевыпустить секрет»: правка адреса эндпоинта заодно ломала
   * уже настроенный на стороне провайдера вебхук.
   */
  function submit(rotateSecret: boolean): void {
    setBusy(true);
    setSecret(undefined);
    saveProvider({
      code: code.trim(),
      kind,
      adapter,
      endpoint: endpoint.trim() ? endpoint.trim() : null,
      credentialEnv: credentialEnv.trim() ? credentialEnv.trim() : null,
      siteId: siteId || null,
      ...('settings' in parsedSettings ? { settings: parsedSettings.settings } : {}),
      rotateSecret,
    })
      .then(async (r) => {
        if (r.secret) setSecret(r.secret);
        setCode('');
        await reload();
        toast.ok(rotateSecret ? t('prov.rotated') : t('prov.saved'));
      })
      .catch((err: Error) => {
        // Раньше ошибка уходила и в onError модуля, и в тост напрямую — на экране
        // появлялись две одинаковые карточки на одно нажатие, вторая с кодом.
        toast.error(err.message, (err as { code?: string }).code);
      })
      .finally(() => setBusy(false));
  }

  const stateOf = (provider: string): ProviderHealth | undefined =>
    health.find((h) => h.provider === provider);

  return (
    <section className="space-y-4">
      <h3 className="text-lg font-semibold text-ink">{t('prov.title')}</h3>

      {secret && (
        <div className="rounded border border-warn-line bg-warn-bg px-3 py-2 text-sm text-warn">
          <div className="font-medium">{t('prov.secretOnce')}</div>
          <code className="mt-1 block break-all font-mono text-xs">{secret}</code>
        </div>
      )}

      {loading ? (
            <TableSkeleton rows={4} columns={5} />
          ) : rows.length === 0 ? (
        <p className="text-ink-muted">{t('prov.empty')}</p>
      ) : (
        <div className="overflow-x-auto rounded border border-line bg-surface">
          <Table className="text-left">
            <thead className="bg-sunken text-xs uppercase text-ink-muted">
              <tr>
                <th className="px-3 py-2">{t('prov.code')}</th>
                <th className="px-3 py-2">{t('prov.kind')}</th>
                <th className="px-3 py-2">{t('prov.adapter')}</th>
                <th className="px-3 py-2">{t('prov.endpoint')}</th>
                <th className="px-3 py-2">{t('prov.credential')}</th>
                <th className="px-3 py-2">{t('prov.status')}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                const state = stateOf(row.code);
                return (
                  <tr key={row.code} className="border-t border-line align-top">
                    <td className="px-3 py-2 font-mono text-xs">{row.code}</td>
                    <td className="px-3 py-2">{t(`prov.kind.${row.kind}` as PaymentsKey)}</td>
                    <td className="px-3 py-2 font-mono text-xs">{row.adapter}</td>
                    <td className="px-3 py-2 font-mono text-xs">{row.endpoint ?? '—'}</td>
                    <td className="px-3 py-2 font-mono text-xs">{row.credentialEnv ?? '—'}</td>
                    <td className="px-3 py-2">
                      <StatusBadge tone={state?.available ? 'ok' : 'danger'}>
                        {state?.available ? t('prov.available') : t('prov.unavailable')}
                      </StatusBadge>
                      {state?.lastError && (
                        <div className="mt-1 text-xs text-ink-muted">{state.lastError}</div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        </div>
      )}

      <div className="rounded border border-line bg-surface p-4">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Field label={t('prov.code')}>
            <TextInput value={code} onChange={(e) => setCode(e.target.value)} />
          </Field>
          <Field label={t('prov.kind')}>
            <Select value={kind} onChange={(e) => setKind(e.target.value as ProviderView['kind'])}>
              <option value="acquiring">{t('prov.kind.acquiring')}</option>
              <option value="wallet">{t('prov.kind.wallet')}</option>
              <option value="invoice">{t('prov.kind.invoice')}</option>
              <option value="fiscal">{t('prov.kind.fiscal')}</option>
            </Select>
          </Field>
          <Field label={t('prov.adapter')}>
            <Select value={adapter} onChange={(e) => setAdapter(e.target.value)}>
              {adapters.map((name) => (
                <option key={name} value={name}>
                  {name}
                </option>
              ))}
            </Select>
          </Field>
          <Field label={t('prov.endpoint')}>
            <TextInput value={endpoint} onChange={(e) => setEndpoint(e.target.value)} />
          </Field>
          <label className="sm:col-span-2">
            <span className={LABEL}>{t('prov.credential')}</span>
            <TextInput
              value={credentialEnv}
              placeholder="MOIKA_PAYMENTS_KASPI_KEY"
              onChange={(e) => setCredentialEnv(e.target.value)}
            />
            <span
              className={`mt-1 block text-xs ${envLooksLikeName ? 'text-ink-muted' : 'text-danger'}`}
            >
              {envLooksLikeName ? t('prov.credentialHint') : t('prov.credentialInvalid')}
            </span>
          </label>
          <Field label={t('prov.site')}>
            <Select value={siteId} onChange={(e) => setSiteId(e.target.value)}>
              <option value="">{t('prov.siteNetwork')}</option>
              {siteList.map((site) => (
                <option key={site.id} value={site.id}>
                  {site.name}
                </option>
              ))}
            </Select>
          </Field>
          <label className="sm:col-span-2">
            <span className={LABEL}>{t('prov.settings')}</span>
            <textarea
              className={`${FIELD} font-mono`}
              rows={3}
              value={settingsText}
              placeholder={'cashboxUniqueNumber=SWK00032685\nvatPercent=12'}
              onChange={(e) => setSettingsText(e.target.value)}
            />
            <span className={`mt-1 block text-xs ${'error' in parsedSettings ? 'text-danger' : 'text-ink-muted'}`}>
              {'error' in parsedSettings
                ? t('prov.settingsInvalid', { line: parsedSettings.error })
                : t('prov.settingsHint')}
            </span>
          </label>
        </div>

        <div className="mt-3 flex flex-wrap gap-2">
          <button
            type="button"
            disabled={busy || !code.trim() || !envLooksLikeName || 'error' in parsedSettings}
            className="inline-flex h-8 items-center justify-center rounded-control bg-brand-600 px-3 text-sm font-medium text-ink-invert shadow-card transition hover:bg-brand-700 disabled:cursor-not-allowed disabled:bg-sunken disabled:text-ink-muted disabled:shadow-none"
            onClick={() => submit(false)}
          >
            {t('prov.save')}
          </button>
          <button
            type="button"
            disabled={busy || !code.trim() || !envLooksLikeName || 'error' in parsedSettings}
            className="inline-flex h-8 items-center justify-center rounded-control border border-line-strong px-3 text-sm font-medium text-ink transition hover:bg-sunken disabled:cursor-not-allowed disabled:text-ink-faint"
            onClick={() =>
              void (async () => {
                const ok = await confirm({
                  title: t('prov.rotateConfirm'),
                  tone: 'danger',
                  confirmLabel: t('prov.rotate'),
                  consequences: [t('prov.rotateEffect'), t('prov.rotateOnce')],
                });
                if (ok) submit(true);
              })()
            }
          >
            {t('prov.rotate')}
          </button>
        </div>
      </div>
    </section>
  );
}
